/**
 * Microsoft 365 Secure Score — Microsoft Graph service layer
 *
 * REQUIRED APP REGISTRATION PERMISSIONS (Entra ID admin consent needed):
 *   Delegated — SecurityEvents.Read.All   → /security/secureScores, /security/secureScoreControlProfiles
 *   Delegated — Organization.Read.All     → /subscribedSkus (licence cross-reference)
 *
 * App registration: a3c0e762-cca9-4ad7-9241-da6299baa336
 * Grant admin consent at: Entra ID > App registrations > API permissions > Grant admin consent
 */

import { authService } from './authService';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

// ── Graph fetch helper ────────────────────────────────────────────────────────

async function graphFetch(path, interactive = false) {
  const token = await authService.getGraphSecurityToken(interactive);
  const res = await fetch(`${GRAPH_BASE}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Graph ${res.status}: ${body || res.statusText}`);
  }
  return res.json();
}

// ── Auth helpers (mirrors azureSecurityService pattern) ──────────────────────

export async function checkSilentAuth() {
  await authService.getGraphSecurityToken(false);
}

export async function connectInteractive() {
  await authService.getGraphSecurityToken(true);
}

// ── Data fetchers ─────────────────────────────────────────────────────────────

/** Latest secure score snapshot. */
export async function getCurrentSecureScore() {
  const data = await graphFetch('/security/secureScores?$top=1');
  return data.value?.[0] ?? null;
}

/** Up to 90 daily snapshots ordered newest-first. */
export async function getScoreHistory() {
  const data = await graphFetch(
    '/security/secureScores?$top=90&$orderby=createdDateTime%20desc'
  );
  return data.value ?? [];
}

/** All control profiles — filtered separately by isControlApplicable(). */
export async function getControlProfiles() {
  // Graph paginates at 100; collect all pages.
  let results = [];
  let url = `${GRAPH_BASE}/security/secureScoreControlProfiles`;
  while (url) {
    const token = await authService.getGraphSecurityToken(false);
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Graph ${res.status}: ${res.statusText}`);
    const page = await res.json();
    results = results.concat(page.value ?? []);
    url = page['@odata.nextLink'] ?? null;
  }
  return results;
}

/** All licence SKUs assigned to the tenant. */
export async function getSubscribedSkus() {
  const data = await graphFetch('/subscribedSkus');
  return data.value ?? [];
}

// ── Licence filtering ────────────────────────────────────────────────────────

/**
 * Builds a flat Set of all service plan names (lowercased) from the
 * subscribedSkus response for use in isControlApplicable().
 */
export function buildEnabledServicePlans(subscribedSkus) {
  const plans = new Set();
  for (const sku of subscribedSkus) {
    for (const plan of sku.servicePlans ?? []) {
      if (plan.servicePlanName) {
        plans.add(plan.servicePlanName.toLowerCase());
      }
    }
  }
  return plans;
}

/**
 * Determines whether a control is applicable to this tenant.
 *
 * Excludes zero-maxScore controls that are still in their default unaddressed
 * state — these are licence placeholders the tenant cannot action.
 *
 * @param {object} control          - secureScoreControlProfile object
 * @param {object[]} controlStateUpdates - control.controlStateUpdates array
 * @param {Set<string>} enabledServicePlans - lowercased plan names from buildEnabledServicePlans() (reserved for future filtering)
 * @returns {boolean}
 */
export function isControlApplicable(control, controlStateUpdates, enabledServicePlans) {
  // Exclude zero-maxScore controls that are still in their default unaddressed state —
  // these are licence placeholders the tenant cannot action.
  // Graph returns state as 'Default' (not 'notImplemented') for unaddressed controls.
  const maxScore = control.maxScore ?? 0;
  if (maxScore <= 0) {
    const updates = controlStateUpdates ?? [];
    const latest = updates.reduce((best, u) =>
      !best || new Date(u.updatedDateTime ?? 0) >= new Date(best.updatedDateTime ?? 0) ? u : best
    , null);
    const state = latest?.state;
    const isActioned = state &&
      !['Default', 'notImplemented', 'noLicenseRequired', 'unknownFutureValue'].includes(state);
    if (!isActioned) return false;
  }
  return true;
}

// ── Derived helpers ───────────────────────────────────────────────────────────

/**
 * Returns true when the tenant does not hold a licence for this control.
 * Primary signal: the enriched hasLicence flag set during loadData by checking
 * whether the control appears in secureScore.controlScores (unlicensed controls
 * are omitted from the tenant score snapshot).
 * Fallback: checks for a noLicenseRequired entry in controlStateUpdates.
 */
export function needsLicence(control) {
  if ('hasLicence' in control) return !control.hasLicence;
  return (control.controlStateUpdates ?? []).some(u => u.state === 'noLicenseRequired');
}

/** Score change (percentage points) over the last N days. Returns null if insufficient history. */
export function getScoreChange(history, days) {
  if (!history.length) return null;
  const latest = history[0];
  const cutoff = new Date(latest.createdDateTime).getTime() - days * 86_400_000;
  const baseline = history.find(s => new Date(s.createdDateTime).getTime() <= cutoff);
  if (!baseline) return null;
  const toPct = s => s.maxScore > 0 ? (s.currentScore / s.maxScore) * 100 : 0;
  return Math.round((toPct(latest) - toPct(baseline)) * 10) / 10;
}
